import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRoleVerify } from "Common/RoleVerify";
import {
  removeMenuList,
  setMenuAccess,
  removeMenuAccess,
} from "../../../Redux/actions/settingsActions";
import { showError, showSuccess } from "utils/notifications";
import useFetchMenu from "Common/FetchAllMenu";
import RoleFetcher from "Common/FetchRolePermission";
import {
  getRolesForPermission,
  getMenusForPermission,
  getRolePermissions,
  updateRolePermissions,
} from "adminApi/RolePermissionApi/RolePermission";

import RoleSelect from "./Componates/RoleSelect";
import RolePermissionSection from "./Componates/RolePermissionSection";

const RESTRICTED_PERMISSIONS = [
  { menu_id: 6, restricted_fields: ["can_create", "can_delete"] },
  { menu_id: 7, restricted_fields: ["can_edit"] },
  { menu_id: 12, restricted_fields: ["can_create", "can_delete"] },
  { menu_id: 13, restricted_fields: ["can_create", "can_delete"] },
  { menu_id: 22, restricted_fields: ["can_delete"] },
  { menu_id: 23, restricted_fields: ["can_delete"] },
  { menu_id: 24, restricted_fields: ["can_delete", "can_edit"] },
  { menu_id: 25, restricted_fields: ["can_delete", "can_edit", "can_create"] },
  { menu_id: 27, restricted_fields: ["can_delete", "can_edit", "can_create"] },
  { menu_id: 28, restricted_fields: ["can_delete", "can_edit", "can_create"] },
  { menu_id: 37, restricted_fields: ["can_create"] },
  { menu_id: 40, restricted_fields: ["can_delete", "can_edit"] },
  { menu_id: 41, restricted_fields: ["can_delete", "can_edit", "can_create"] },
  { menu_id: 42, restricted_fields: ["can_delete", "can_edit", "can_create"] },
  { menu_id: 47, restricted_fields: ["can_delete", "can_edit", "can_create"] },
  { menu_id: 48, restricted_fields: ["can_delete", "can_edit", "can_create"] },


];

const RolePermissionList = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const rolePermission = useRoleVerify("", ["can_edit"]);
  const { refetch } = useFetchMenu("tunning");

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [parentMenu, setParentMenu] = useState([]);
  const [menuList, setMenuList] = useState([]);
  const [roleMenuAccess, setRoleMenuAccess] = useState({});
  const [loading, setLoading] = useState(false);

  const canEditPermissions = rolePermission && selectedRole;

  // ===== FETCH ROLES =====
  useEffect(() => {
    (async () => {
      try {
        const res = await getRolesForPermission();
        if (res.data.success) setRoles(res.data.data?.data || []);
      } catch (err) {
        console.error("Error fetching roles:", err);
      }
    })();
  }, []);

  // ===== FETCH MENUS =====
  useEffect(() => {
    (async () => {
      try {
        const res = await getMenusForPermission();
        if (res.data.success) {
          const data = res.data.data;
          setParentMenu(data.parent_menu || []);
          setMenuList(data.menulist || []);
          const initial = {};
          (data.menulist || []).forEach((menu) => {
            initial[menu.menu_id] = {
              menu_id: menu.menu_id,
              can_access: false,
              can_create: false,
              can_edit: false,
              can_delete: false,
            };
          });
          setRoleMenuAccess(initial);
        }
      } catch (err) {
        console.error("Error fetching menus:", err);
      }
    })();
  }, []);

  // ===== FETCH ROLE PERMISSIONS =====
  const fetchRolePermissions = async (roleId) => {
    if (!roleId) return;
    try {
      const res = await getRolePermissions({ id: roleId });
      if (res.data.success) {
        const apiPerms = res.data.data.data;
        setRoleMenuAccess((prev) => {
          const merged = { ...prev };
          apiPerms.forEach((p) => {
            merged[p.menu_id] = { ...merged[p.menu_id], ...p };
          });
          return merged;
        });
      }
    } catch (err) {
      console.error("Error fetching role permissions:", err);
    }
  };

  // ===== HANDLERS =====
  const handleRoleChange = (e) => {
    const val = e.target.value;
    setSelectedRole(val);
    fetchRolePermissions(val);
  };

  const handlePermissionChange = (menuId, field, checked) => {
    setRoleMenuAccess((prev) => ({
      ...prev,
      [menuId]: { ...prev[menuId], [field]: checked },
    }));
  };

  const handleParentPermissionChange = (parentId, field, checked) => {
    setRoleMenuAccess((prev) => {
      const updated = { ...prev };

      menuList
        .filter((menu) => menu.parent_menu === parentId)
        .forEach((menu) => {
          if (field === "ALL") {
            updated[menu.menu_id] = {
              ...updated[menu.menu_id],
              can_access: checked,
              can_create: checked,
              can_edit: checked,
              can_delete: checked,
            };
          } else {
            updated[menu.menu_id] = {
              ...updated[menu.menu_id],
              [field]: checked,
            };
          }
        });

      return updated;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!rolePermission) return showError("You don't have permission to perform this action.");
    if (!selectedRole) return showError("Please select a role first.");
    setLoading(true);
    const payload = {
      permissions: Object.values(roleMenuAccess).map((p) => {
        const restriction = RESTRICTED_PERMISSIONS.find((r) => r.menu_id === Number(p.menu_id));
        const overrides = restriction
          ? restriction.restricted_fields.reduce((acc, field) => ({ ...acc, [field]: false }), {})
          : {};

        return {
          ...p,
          role_id: selectedRole,
          ...overrides,
        };
      }),
    };
    try {
      const res = await updateRolePermissions({ data: payload });
      if (!res.data.success) {
        showError(res.data.error || "Failed to save permissions.");
      } else {
        dispatch(removeMenuList());
        await refetch();
        showSuccess(res.data.message);
        RoleFetcher({ dispatch, user, setMenuAccess, removeMenuAccess });
      }
    } catch {
      showError("Error updating permissions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3">
        <div className="col-sm-12">
          <h3 className="fw-bold mb-0">Manage Role Permission</h3>
        </div>

        <div className="col-sm-12">
          <div className="card">
            {!selectedRole && (
              <div className="card-header border-0 pb-0">

                <div
                  className="alert alert-info d-flex align-items-center mb-0 mt-2 fade show border-0 w-100 border-dashed"
                  role="alert"
                >
                  <i className="bi bi-shield-lock-fill me-3 fs-4 text-info"></i>
                  <div>
                    <strong className="d-block">You must select a role before continuing</strong>
                  </div>
                </div>

              </div>
            )}
            <div className="card-body">
              <RoleSelect
                roles={roles}
                selectedRole={selectedRole}
                onChange={handleRoleChange}
              />

              <RolePermissionSection
                parentMenu={parentMenu}
                menuList={menuList}
                roleMenuAccess={roleMenuAccess}
                rolePermissions={canEditPermissions}
                rolePermission={rolePermission}
                onPermissionChange={handlePermissionChange}
                onParentPermissionChange={handleParentPermissionChange}
                RESTRICTED_PERMISSIONS={RESTRICTED_PERMISSIONS}
              />
            </div>

            {rolePermission && (
              <div
                className="card-footer text-end sticky-bottom bg-body border-top shadow-sm py-3"
                style={{
                  zIndex: 10,
                  bottom: 0,
                  margin: '0 -1px -1px -1px' // ensures it aligns perfectly with card borders
                }}
              >
                <button
                  type="submit"
                  className="btn btn-primary px-4 fw-bold"
                  onClick={handleSave}
                  disabled={!selectedRole || loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      Save Permissions
                    </>
                  )}
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default RolePermissionList;
